import React, { useState, useRef, useEffect } from "react";
import './terminal.css'
import useReveal from "../../hooks/useReveal";
import CV from "../../assets/sumit_kumar_resume.pdf";

const COMMANDS = {
    help: () => (
        <div>
            <p>Available commands:</p>
            <p><span className="t-cmd">about</span>      — who I am</p>
            <p><span className="t-cmd">skills</span>     — my tech stack</p>
            <p><span className="t-cmd">work</span>       — selected professional work</p>
            <p><span className="t-cmd">projects</span>   — side projects</p>
            <p><span className="t-cmd">experience</span> — career timeline</p>
            <p><span className="t-cmd">contact</span>    — how to reach me</p>
            <p><span className="t-cmd">resume</span>     — download my CV</p>
            <p><span className="t-cmd">social</span>     — links</p>
            <p><span className="t-cmd">whoami</span> · <span className="t-cmd">clear</span> · <span className="t-cmd">date</span></p>
        </div>
    ),
    about: () => (
        <p>Senior Backend &amp; Data Engineer, 7+ years. I build terabyte-scale data
        platforms and production LLM infrastructure — currently architecting the data
        platform behind an AI brand-analytics product at BrightEdge.</p>
    ),
    whoami: () => <p>sumit_kumar — Senior Backend &amp; Data Engineer (Jamshedpur, IN · remote-first)</p>,
    skills: () => (
        <div>
            <p><span className="t-key">languages</span>  Python, SQL, JavaScript</p>
            <p><span className="t-key">backend</span>    FastAPI, Django, Flask</p>
            <p><span className="t-key">data</span>       ClickHouse, Trino, Apache Iceberg, BigQuery, Parquet</p>
            <p><span className="t-key">infra</span>      GCP, AWS, Kubernetes, Terraform, Argo Workflows</p>
            <p><span className="t-key">ai/ml</span>      LLM fine-tuning, GPU infra, TensorFlow, Vector Search</p>
        </div>
    ),
    work: () => (
        <div>
            <p>• <span className="t-key">BrightEdge</span> — AI brand-analytics data platform (ClickHouse, Trino+Iceberg, terabyte-scale)</p>
            <p>• Scalable GCP web-crawling infra (Terraform/Ansible, millions of pages/day)</p>
            <p>• High-volume crawl analytics (Parquet → GCS → BigQuery → ClickHouse)</p>
            <p>• Greendeck — monolith → microservices + multimodal product matching</p>
            <p className="t-muted">→ see the "Selected Work" section for details</p>
        </div>
    ),
    projects: () => (
        <div>
            <p>• <span className="t-key">PlayRoom</span> — real-time multiplayer Charades + Scribble <a href="https://playroom.skdev.one" target="_blank" rel="noreferrer">playroom.skdev.one</a></p>
            <p>• <span className="t-key">MoneyFlow</span> — finance tracker, auto-imports Gmail + bank PDFs <a href="https://moneyflow.skdev.one" target="_blank" rel="noreferrer">moneyflow.skdev.one</a></p>
            <p>• <span className="t-key">AI Trading Agent</span> — autonomous NSE/BSE paper-trading agent <a href="https://alphaagent.skdev.one" target="_blank" rel="noreferrer">alphaagent.skdev.one</a></p>
            <p>• <span className="t-key">SocialFlow</span> — AI conversation practice for social skills <a href="https://socialflow.skdev.one" target="_blank" rel="noreferrer">socialflow.skdev.one</a></p>
        </div>
    ),
    experience: () => (
        <div>
            <p><span className="t-key">2025–now</span>  BrightEdge — Senior Backend &amp; Data Engineer</p>
            <p><span className="t-key">2021–2025</span> Mindfire Solutions — Senior Backend &amp; Data Engineer</p>
            <p><span className="t-key">2019–2020</span> Greendeck — ML / Data Engineer</p>
        </div>
    ),
    contact: () => (
        <div>
            <p>email    <a href="mailto:rsumit123@gmail.com">rsumit123@gmail.com</a></p>
            <p>linkedin <a href="https://linkedin.com/in/rsumit123" target="_blank" rel="noreferrer">linkedin.com/in/rsumit123</a></p>
            <p>github   <a href="https://github.com/rsumit123" target="_blank" rel="noreferrer">github.com/rsumit123</a></p>
        </div>
    ),
    social: () => COMMANDS.contact(),
    resume: () => (
        <p>Opening CV… <a href={CV} download="Sumit_Kumar_Resume.pdf">download sumit_kumar_resume.pdf</a></p>
    ),
    date: () => <p>{new Date().toString()}</p>,
    sudo: () => <p className="t-err">Permission denied: nice try 😏</p>,
    ls: () => <p>about  skills  work  projects  experience  contact  resume</p>,
    pwd: () => <p>/home/sumit/portfolio</p>,
};
COMMANDS.cv = COMMANDS.resume;

const BANNER = "Type 'help' to see what you can do. Try 'about' or 'skills'.";

const InteractiveTerminal = () => {
    const ref = useReveal();
    const [history, setHistory] = useState([{ input: null, output: <p className="t-muted">{BANNER}</p> }]);
    const [value, setValue] = useState("");
    const [past, setPast] = useState([]);
    const [pastIdx, setPastIdx] = useState(-1);
    const inputRef = useRef(null);
    const bodyRef = useRef(null);

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }, [history]);

    const run = (raw) => {
        const cmd = raw.trim().toLowerCase();
        if (!cmd) return setHistory((h) => [...h, { input: raw, output: null }]);
        if (cmd === "clear") { setHistory([]); return; }

        let output;
        const [base, ...rest] = cmd.split(" ");
        if (base === "echo") {
            output = <p>{rest.join(" ")}</p>;
        } else if (COMMANDS[base]) {
            output = COMMANDS[base]();
        } else {
            output = <p className="t-err">command not found: {cmd} — type 'help'</p>;
        }
        setHistory((h) => [...h, { input: raw, output }]);
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") {
            run(value);
            if (value.trim()) setPast((p) => [value, ...p]);
            setValue("");
            setPastIdx(-1);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setPastIdx((i) => {
                const ni = Math.min(i + 1, past.length - 1);
                if (past[ni] !== undefined) setValue(past[ni]);
                return ni;
            });
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            setPastIdx((i) => {
                const ni = Math.max(i - 1, -1);
                setValue(ni === -1 ? "" : past[ni]);
                return ni;
            });
        }
    };

    return (
        <section id="terminal">
            <h5>$ ./explore.sh</h5>
            <h2>Try the Terminal</h2>
            <div className="container reveal" ref={ref}>
                <div
                    className="iterm term-window"
                    onClick={() => inputRef.current && inputRef.current.focus()}
                >
                    <div className="term-titlebar">
                        <span className="term-dot red"></span>
                        <span className="term-dot yellow"></span>
                        <span className="term-dot green"></span>
                        <span className="term-path">sumit@portfolio: ~ — bash</span>
                    </div>
                    <div className="iterm__body" ref={bodyRef}>
                        {history.map((entry, i) => (
                            <div key={i} className="iterm__entry">
                                {entry.input !== null && (
                                    <p className="iterm__line"><span className="t-prompt">visitor@skdev:~$</span> {entry.input}</p>
                                )}
                                {entry.output && <div className="iterm__output">{entry.output}</div>}
                            </div>
                        ))}
                        <div className="iterm__inputline">
                            <span className="t-prompt">visitor@skdev:~$</span>
                            <input
                                ref={inputRef}
                                className="iterm__input"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                onKeyDown={onKeyDown}
                                spellCheck="false"
                                autoComplete="off"
                                aria-label="Terminal command input"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default InteractiveTerminal
